import {
	Box,
	Button,
	Inline,
	Icons,
	Stack,
	Heading,
	Col,
	Divider,
	Grid,
	joinAttributes,
	Text,
	Select,
	CheckboxInput,
} from "@sembark-travel/ui-base"
import { Dialog } from "@sembark-travel/ui-dialog"
import { Markdown } from "@sembark-travel/ui-markdown"
import {
	clone,
	formatDate,
	humanizeDuration,
	setUnit,
} from "@sembark-travel/datetime-utils"
import { useXHR } from "@sembark-travel/xhr"
import {
	Form,
	TextInputField,
	MarkdownInputField,
	withServerErrors,
	SubmissionError,
	validateFormValues,
	SelectField,
	FormSpy,
	EmptyNumberValidator,
	DatePickerField,
	CheckboxInputField,
	SwitchInputField,
	GetFieldValue,
	FieldArray,
	arrayMutators,
	DateRangePickerField,
	isTruthy,
	useForm,
	useFieldValue,
} from "@sembark-travel/ui-form"
import { Optional } from "utility-types"
import * as Validator from "yup"
import { SelectTripDestination } from "../TripDestinations"
import { useAuthUser } from "../Auth"
import { useEffect, useMemo, useState } from "react"
import config from "../config"
import { SelectTransportServiceLocationPoints } from "./Points"
import {
	ITransportService,
	NewServiceCredentials,
	TTransportServiceLocationPoint,
} from "./store"

const INITIAL_VALUES: NewServiceCredentials = {
	from: undefined,
	to: undefined,
	short_code: "",
	trip_destinations: [],
	use_same_locations: true,
	pick_up_locations: [undefined],
	drop_locations: [undefined],
	transport_service: [
		{
			service: "",
			service_short_name: "",
			distance: 0,
			start_time: undefined,
			duration: undefined,
			service_details: "",
			pick_up_locations: [undefined],
			drop_locations: [undefined],
			set_hotel_as_default_pickup: null,
			set_hotel_as_default_drop: null,
		},
	],
	set_hotel_as_default_pickup: null,
	set_hotel_as_default_drop: null,
}

export function NewServicesForm({
	initialValues: propInitialValues,
	onSuccess,
	onCancel,
	type,
	serviceLocationId,
	canAddMoreServices,
	url,
	readOnlyLocationFields = false,
}: {
	initialValues?: Optional<NewServiceCredentials>
	onCancel?: () => void
	onSuccess: (transportService: ITransportService) => void
	type: "EditSingleService" | "addLocationService"
	serviceLocationId: number | undefined
	canAddMoreServices: boolean
	url: string
	readOnlyLocationFields?: boolean
}) {
	const xhr = useXHR()
	const { user } = useAuthUser()
	const can_attach_services_to_destinations = Boolean(
		user?.tenant?.feature_flags?.attach_services_to_destinations
	)
	const validationSchema = validateFormValues(
		Validator.object().shape({
			from: Validator.object().required(
				"Please provide the pickup / start location of service."
			),
			to: Validator.object().nullable(),
			transport_service: Validator.array()
				.of(
					Validator.object({
						service: Validator.string()
							.required("Please provide the name or type of the service.")
							.max(
								191,
								"Please keep the service name within 191 characters for better readability."
							),

						service_short_name: Validator.string()
							.nullable()
							.max(191, "Please use 191 or fewer characters for code name."),

						distance: EmptyNumberValidator()
							.typeError("Distance should be a number")
							.integer("Distance should be an integer")
							.min(0, "Please use a non negative number"),

						duration: EmptyNumberValidator()
							.typeError("Duration should be a number")
							.integer("Duration should be an integer"),

						service_details: Validator.string()
							.nullable()
							.max(10000, "Please use less than 10000 characters"),
					})
				)
				.test("unique-service-fields", "", function (items) {
					if (!items) return true

					const { path, createError } = this
					const errors:
						| Validator.ValidationError
						| Validator.ValidationError[] = []

					const serviceMap = new Map() // service → first index
					const duplicates: {
						index: number
						service: string
						code: string
						serviceDuplicate: boolean | ""
					}[] = []

					items.forEach((row, index) => {
						const service = row.service?.trim().toLowerCase() || ""
						const code = row.service_short_name?.trim().toLowerCase() || ""

						const serviceDuplicate = service && serviceMap.has(service + code)

						if (serviceDuplicate) {
							duplicates.push({
								index,
								service,
								code,
								serviceDuplicate,
							})
						}

						// Register first occurrence
						if (service && !serviceDuplicate)
							serviceMap.set(service + code, index)
					})

					duplicates.forEach((dup) => {
						const firstServiceIndex = serviceMap.get(dup.service + dup.code)

						if (dup.serviceDuplicate && dup.service) {
							errors.push(
								createError({
									path: `${path}[${dup.index}].service`,
									message:
										"Service Name already exists. Change Service Name or Service Code.",
								})
							)
							if (dup.code) {
								errors.push(
									createError({
										path: `${path}[${dup.index}].service_short_name`,
										message:
											"Service Code already exists. Change Service Name or Service Code.",
									})
								)
							}
							// first row errors (optional)
							errors.push(
								createError({
									path: `${path}[${firstServiceIndex}].service`,
									message:
										"Service Name already exists. Change Service Name or Service Code.",
								})
							)
							if (dup.code) {
								errors.push(
									createError({
										path: `${path}[${firstServiceIndex}].service_short_name`,
										message:
											"Service Code already exists. Change Service Name or Service Code.",
									})
								)
							}
							return
						}
					})

					if (errors.length > 0) {
						throw new Validator.ValidationError(errors)
					}

					return true
				}),
			trip_destinations:
				can_attach_services_to_destinations &&
				type !== "EditSingleService" &&
				!readOnlyLocationFields
					? Validator.array()
							.required("Please select a Destinations")
							.min(1, "Please select atleast one Destinations")
					: Validator.array().nullable(),
			quick_add: Validator.boolean(),
		})
	)
	const initialValues = useMemo<NewServiceCredentials>(() => {
		const InitialProp = propInitialValues || {}
		const { trip_destinations, ...restData } = InitialProp
		return {
			...INITIAL_VALUES,
			...propInitialValues,
			quick_add:
				InitialProp?.quick_add ?? !Object.values(restData || {}).length,
		}
	}, [propInitialValues])
	return (
		<Form<NewServiceCredentials>
			initialValues={initialValues}
			validate={validationSchema}
			onSubmit={withServerErrors(async (values) => {
				const {
					from,
					to,
					short_code,
					trip_destinations,
					quick_add,
					transport_service,
					use_same_locations,
				} = values

				// When switch is checked, use shared top-level locations for all services
				const sharedPickUpLocations =
					use_same_locations && type === "addLocationService"
						? values.pick_up_locations
								?.map((l) => (typeof l === "object" ? l?.name : l))
								.filter(Boolean)
						: undefined
				const sharedDropLocations =
					use_same_locations && type === "addLocationService"
						? values.drop_locations
								?.map((l) => (typeof l === "object" ? l?.name : l))
								.filter(Boolean)
						: undefined
				const sharedSetHotelAsDefaultPickup =
					use_same_locations && type === "addLocationService"
						? values.set_hotel_as_default_pickup
						: undefined
				const sharedSetHotelAsDefaultDrop =
					use_same_locations && type === "addLocationService"
						? values.set_hotel_as_default_drop
						: undefined

				const services = transport_service?.map((s) => {
					const effectivePickUpLocations =
						sharedPickUpLocations ??
						s.pick_up_locations
							?.map((l) => (typeof l === "object" ? l?.name : l))
							.filter(Boolean)
					const effectiveDropLocations =
						sharedDropLocations ??
						s.drop_locations
							?.map((l) => (typeof l === "object" ? l?.name : l))
							.filter(Boolean)
					const effectiveSetHotelAsDefaultPickup =
						sharedSetHotelAsDefaultPickup ?? s.set_hotel_as_default_pickup
					const effectiveSetHotelAsDefaultDrop =
						sharedSetHotelAsDefaultDrop ?? s.set_hotel_as_default_drop

					return {
						service: s.service,
						service_short_name: quick_add ? undefined : s.service_short_name,
						distance: s.distance || 0,
						start_time:
							s.start_time && !quick_add
								? formatDate(
										setUnit(clone(s.start_time), "seconds", 0),
										config.timeFormat
									)
								: undefined,
						duration: quick_add ? undefined : s.duration,
						closed_on_days_of_week: isTruthy(quick_add)
							? []
							: s.closed_on_days_of_week,
						closed_on_date_intervals: (isTruthy(quick_add)
							? []
							: s.closed_on_date_intervals || []
						)
							.filter((d): d is Required<typeof d> =>
								Boolean(d.start_date || d.end_date)
							)
							.map((d) => {
								const start_date = d.start_date
									? formatDate(d.start_date, "YYYY-MM-DD")
									: undefined
								const end_date = d.end_date
									? formatDate(d.end_date, "YYYY-MM-DD")
									: undefined

								if (start_date || end_date) {
									return [start_date || end_date, end_date || start_date] as [
										string,
										string,
									]
								}
								return undefined
							})
							.filter(
								(d): d is Exclude<typeof d, undefined> => d !== undefined
							),
						service_details: s.service_details,
						pick_up_locations: effectivePickUpLocations,
						drop_locations: effectiveDropLocations,
						set_hotel_as_default_pickup: effectiveSetHotelAsDefaultPickup,
						set_hotel_as_default_drop: effectiveSetHotelAsDefaultDrop,
					}
				})

				const allServices =
					type === "EditSingleService" && services?.length
						? services[0]
						: { transport_service: services }
				const {
					data: { data: transportService },
				} = await xhr.post<{ data: ITransportService }>(url, {
					from: from?.name,
					to: to?.name,
					trip_destinations:
						trip_destinations?.length && can_attach_services_to_destinations
							? trip_destinations.map((t) => t.id)
							: [],
					short_code: short_code && !quick_add ? String(short_code) : undefined,
					service_location_id: serviceLocationId ?? undefined,
					...allServices,
				})
				onSuccess(transportService)
			})}
			subscription={{ submitting: true }}
			mutators={{ ...arrayMutators }}
		>
			{({ submitting, handleSubmit }) => (
				<form noValidate onSubmit={handleSubmit}>
					<GetFieldValue<boolean> name={`quick_add`}>
						{({ value: quickAdd }) => (
							<>
								{type === "EditSingleService" ||
								readOnlyLocationFields ? null : (
									<Grid>
										<Col sm={12} md={{ offset: 4, span: 8 }}>
											<Inline paddingBottom="2" justifyContent="end">
												<Box
													paddingX={"2"}
													paddingY={"1"}
													borderRadius="md"
													borderWidth={"2"}
													borderColor={"primary"}
													bgColor={quickAdd ? "primary" : "accent"}
													cursor={
														!quickAdd || submitting ? "disabled" : "pointer"
													}
												>
													<CheckboxInputField
														label={"Quick Add"}
														name={`quick_add`}
														disabled={!quickAdd || submitting}
													/>
												</Box>
											</Inline>
										</Col>
									</Grid>
								)}
								<Grid gap="4">
									<Col sm={12} md={4}>
										<Inline gap="2">
											<Box>
												<Icons.LocationMarker size="6" />
											</Box>
											<Stack>
												<Heading as="h3">Locations</Heading>
												<Text color="muted">
													Add pickup-drop location along with a short code for
													quick identification.
												</Text>
											</Stack>
										</Inline>
									</Col>
									{type === "addLocationService" && !readOnlyLocationFields ? (
										<Col>
											<Stack gap="4">
												<Grid gap="4">
													<Col>
														<SelectField
															select={SelectTransportServiceLocationPoints}
															label="Start City"
															name="from"
															required
															multiple={false}
															creatable
														/>
													</Col>
													<Col>
														<SelectField
															select={SelectTransportServiceLocationPoints}
															label="End City"
															name="to"
															secondaryLabel="optional"
															multiple={false}
															creatable
														/>
													</Col>
												</Grid>
												<Grid gap="4">
													{quickAdd ? null : (
														<Col>
															<TextInputField
																type="text"
																label="Short Code"
																name="short_code"
																secondaryLabel="optional"
																placeholder="125"
															/>
														</Col>
													)}
													{can_attach_services_to_destinations ? (
														<Col>
															<SelectField
																select={SelectTripDestination}
																name="trip_destinations"
																label="Trip Destinations"
																multiple
																closeOnSelect
															/>
														</Col>
													) : null}
												</Grid>
												{quickAdd ? null : (
													<>
														<SwitchInputField
															name="use_same_locations"
															label="Use same pick-up/drop point for All Services"
														/>
														<GetFieldValue<boolean> name="use_same_locations">
															{({ value: useSameLocations }) =>
																useSameLocations ? (
																	<Inline gap="6" flexWrap="wrap">
																		<Stack gap="3">
																			<Inline gap="1" alignItems="center">
																				<Text as="label" fontWeight="semibold">
																					Pick Up Locations
																				</Text>
																				<Text fontSize="sm" color="muted">
																					(optional)
																				</Text>
																			</Inline>
																			<LocationFields name="pick_up_locations" />
																			<HotelAsPickupDropToggleField
																				name="set_hotel_as_default_pickup"
																				for="pickup"
																			/>
																		</Stack>
																		<Stack gap="3">
																			<Inline gap="1" alignItems="center">
																				<Text as="label" fontWeight="semibold">
																					Drop Locations
																				</Text>
																				<Text fontSize="sm" color="muted">
																					(optional)
																				</Text>
																			</Inline>
																			<LocationFields name="drop_locations" />
																			<HotelAsPickupDropToggleField
																				name="set_hotel_as_default_drop"
																				for="drop"
																			/>
																		</Stack>
																	</Inline>
																) : null
															}
														</GetFieldValue>
													</>
												)}
											</Stack>
										</Col>
									) : (
										<Col>
											<Inline flexWrap={"wrap"} gap={"2"}>
												<Stack gap="1">
													<Box
														color="muted"
														fontSize="sm"
														letterSpacing="wider"
														fontWeight="semibold"
													>
														Start City
													</Box>
													<Box fontSize="sm">{initialValues.from?.name}</Box>
												</Stack>
												<Divider sm />
												<Stack
													borderLeftWidth={"2"}
													borderLeftColor={"default"}
													paddingLeft={"2"}
													gap="1"
												>
													<Box
														color="muted"
														fontWeight="semibold"
														fontSize="sm"
														letterSpacing="wider"
													>
														End City
													</Box>
													<Box fontSize="sm">{initialValues.to?.name}</Box>
												</Stack>
												<Stack
													borderLeftWidth={"2"}
													borderLeftColor={"default"}
													paddingLeft={"2"}
													gap="1"
												>
													<Box
														color="muted"
														fontWeight="semibold"
														fontSize="sm"
														letterSpacing="wider"
													>
														Short Code
													</Box>
													<Box fontSize="sm">{initialValues.short_code}</Box>
												</Stack>
												<Stack
													borderLeftWidth={"2"}
													borderLeftColor={"default"}
													paddingLeft={"2"}
													gap="1"
												>
													<Box
														color="muted"
														fontWeight="semibold"
														fontSize="sm"
														letterSpacing="wider"
													>
														Trip Destination
													</Box>
													<Box fontSize="sm">
														{initialValues.trip_destinations?.length
															? initialValues.trip_destinations
																	?.map((td) => td.name)
																	.join(", ")
															: "All"}
													</Box>
												</Stack>
											</Inline>
										</Col>
									)}
								</Grid>
								<Divider sm />
								<Grid gap="4">
									<Col sm={12} md={4}>
										<Inline gap="2">
											<Box>
												<Icons.Tags size="6" />
											</Box>
											<Stack gap="1">
												<Heading as="h3">
													{canAddMoreServices
														? "Service and Itinerary Details"
														: "Service Details"}
												</Heading>
												<Text color="muted">
													{canAddMoreServices
														? "Please provide the service name with inclusions, distance, duration and itinerary description."
														: "Please provide the service name with inclusions distance and duration."}
												</Text>
											</Stack>
										</Inline>
									</Col>
									<Col>
										<FieldArray<
											Partial<ITransportService> | NewServiceCredentials
										> name="transport_service">
											{({ fields }) => {
												return (
													<>
														{fields.map((name, index) => (
															<Box display={"flex"} key={name}>
																{(fields?.length ?? 0) > 1 ? (
																	<Box
																		borderWidth={"1"}
																		borderColor={"muted"}
																		flex={"initial"}
																		width={"8"}
																		textAlign={"center"}
																		verticalAlign="middle"
																		paddingTop={"4"}
																		fontWeight="semibold"
																		bgColor={"inset"}
																	>
																		{index + 1}
																	</Box>
																) : null}
																<Stack
																	paddingTop={"2"}
																	gap={(fields?.length ?? 0) > 1 ? "4" : "0"}
																	flex={"1"}
																>
																	<Stack
																		gap={"6"}
																		paddingLeft={
																			(fields?.length ?? 0) > 1 ? "4" : "0"
																		}
																	>
																		<TextInputField
																			type="text"
																			label="Service Name"
																			name={`${name}.service`}
																			placeholder="Onway sightseeing"
																		/>
																		{quickAdd ? null : (
																			<>
																				<TextInputField
																					type="text"
																					label="Service Code Name"
																					name={`${name}.service_short_name`}
																					secondaryLabel="optional"
																					placeholder="e.g. SUPPLIER OR SPECIFIC HIDDEN DETAILS"
																					help="You can attach a code to the service for any unique and non customer sharable information."
																				/>
																				<GetFieldValue<boolean> name="use_same_locations">
																					{({ value: useSameLocations }) =>
																						!useSameLocations ||
																						type === "EditSingleService" ? (
																							<Inline gap="4" flexWrap="wrap">
																								<Stack gap="3">
																									<Inline
																										gap="1"
																										alignItems="center"
																									>
																										<Text
																											as="label"
																											fontWeight="semibold"
																										>
																											Pick Up Locations
																										</Text>
																										<Text
																											fontSize="sm"
																											color="muted"
																										>
																											(optional)
																										</Text>
																									</Inline>
																									<LocationFields
																										name={`${name}.pick_up_locations`}
																									/>
																									<HotelAsPickupDropToggleField
																										name={`${name}.set_hotel_as_default_pickup`}
																										for="pickup"
																									/>
																								</Stack>
																								<Stack gap="3">
																									<Inline
																										gap="1"
																										alignItems="center"
																									>
																										<Text
																											as="label"
																											fontWeight="semibold"
																										>
																											Drop Locations
																										</Text>
																										<Text
																											fontSize="sm"
																											color="muted"
																										>
																											(optional)
																										</Text>
																									</Inline>
																									<LocationFields
																										name={`${name}.drop_locations`}
																									/>
																									<HotelAsPickupDropToggleField
																										name={`${name}.set_hotel_as_default_drop`}
																										for="drop"
																									/>
																								</Stack>
																							</Inline>
																						) : null
																					}
																				</GetFieldValue>
																				<Grid justifyContent={"evenly"} gap="4">
																					<Col>
																						<TextInputField
																							name={`${name}.distance`}
																							type="number"
																							label="Distance (kms)"
																							secondaryLabel="optional"
																							placeholder="420"
																						/>
																					</Col>
																					<Col>
																						<DatePickerField
																							label="Start Time"
																							name={`${name}.start_time`}
																							dateFormat={false}
																							timeFormat={
																								config.timeDisplayFormat
																							}
																							secondaryLabel="optional"
																							help="Default start time for the service"
																						/>
																					</Col>
																					<Col>
																						<TextInputField
																							name={`${name}.duration`}
																							type="number"
																							label="Duration (in mins)"
																							secondaryLabel="optional"
																							placeholder="60"
																						/>
																					</Col>
																				</Grid>
																			</>
																		)}
																		<GetFieldValue<
																			"" | boolean
																		> name="quick_add">
																			{({ value }) =>
																				isTruthy(value) ? null : (
																					<>
																						<SelectClosedOnDaysOfWeekField
																							name={`${name}.closed_on_days_of_week`}
																						/>
																						<SelectClosedOnDateIntervalsField
																							name={`${name}.closed_on_date_intervals`}
																						/>
																					</>
																				)
																			}
																		</GetFieldValue>
																		{canAddMoreServices ? (
																			<>
																				<MarkdownInputField
																					name={`${name}.service_details`}
																					label="Description"
																					placeholder="Upon your arrival at Railway Station, your driver will escort you to your hotel..."
																				/>

																				<Box>
																					{(fields?.length ?? 0) > 1 ? (
																						<Button
																							onClick={() =>
																								fields.remove(index)
																							}
																							title="Remove"
																							size="sm"
																							level="tertiary"
																						>
																							<Icons.Cancel /> Remove
																						</Button>
																					) : null}
																				</Box>
																			</>
																		) : null}
																	</Stack>
																	{canAddMoreServices ? (
																		<Divider
																			marginTop={"0"}
																			marginBottom={"0"}
																			sm
																		/>
																	) : null}
																</Stack>
															</Box>
														))}
														{canAddMoreServices ? (
															<Box
																paddingLeft={
																	(fields?.length ?? 0) > 1 ? "8" : "0"
																}
																marginTop={"4"}
															>
																<Button
																	onClick={() =>
																		fields.push({
																			service: "",
																			service_short_name: "",
																			distance: 0,
																			start_time: undefined,
																			duration: undefined,
																			pick_up_locations: [undefined],
																			drop_locations: [undefined],
																		})
																	}
																	level="secondary"
																	status="primary"
																	title="Add another service"
																>
																	<Icons.Plus /> Add another service
																</Button>
															</Box>
														) : null}
													</>
												)
											}}
										</FieldArray>
									</Col>
								</Grid>
								{canAddMoreServices ? null : (
									<>
										<Divider />
										<Grid gap="4">
											<Col sm={12} md={4}>
												<Inline gap="2">
													<Box>
														<Icons.Cog size="6" />
													</Box>
													<Stack gap="1">
														<Heading as="h3">Itinerary</Heading>
														<Text color="muted">
															You can (optionally) provide details for this
															service. This will allow autofilling day-wise
															schedule in an itinerary when this service is
															provided in a quote.
														</Text>
													</Stack>
												</Inline>
											</Col>
											<Col>
												<MarkdownInputField
													label="Description"
													name="transport_service[0].service_details"
													placeholder="Upon your arrival at Railway Station, your driver will escort you to your hotel..."
												/>
											</Col>
										</Grid>
									</>
								)}
								<Divider />

								<FormSpy<NewServiceCredentials>>
									{({ values }) => {
										const { short_code, from, to, transport_service } = values
										return transport_service?.map((service, ind) => (
											<Box
												padding="4"
												boxShadow="lg"
												rounded="lg"
												borderWidth="1"
												bgColor="subtle"
												marginY="4"
												key={service.service + ind}
											>
												<Box
													textTransform="uppercase"
													fontSize="sm"
													color="muted"
													marginBottom="4"
													fontWeight="semibold"
													letterSpacing="wider"
												>
													Summary
												</Box>
												<Box>
													{!from ? (
														<Heading color="muted">[Title]</Heading>
													) : (
														<Box>
															{short_code ? (
																<Box as="code" marginRight="1" display="block">
																	Code {short_code}
																</Box>
															) : null}
															<Heading>
																{from.name}
																{to ? (
																	<Box as="span" marginLeft="1">
																		to {to.name}
																	</Box>
																) : null}
																{service.service ? (
																	<Box as="span" marginLeft="1">
																		- {service.service}
																	</Box>
																) : null}
															</Heading>
														</Box>
													)}
												</Box>
												<Text>
													{joinAttributes(
														service.distance ? `${service.distance} kms` : null,
														service.start_time
															? formatDate(service.start_time, "HH:mm") + " hrs"
															: null,
														service.duration
															? `${humanizeDuration(
																	service.duration * 60 * 1000,
																	{
																		short: false,
																	}
																)}`
															: null
													)}
												</Text>
												<Box color="muted">
													{service.service_details ? (
														<Markdown>{service.service_details}</Markdown>
													) : (
														"[Itinerary]"
													)}
												</Box>
											</Box>
										))
									}}
								</FormSpy>
							</>
						)}
					</GetFieldValue>
					<Divider sm />
					<Grid>
						<Col sm={12} md={{ offset: 4, span: 8 }}>
							<Stack gap="4">
								<SubmissionError />
								<Inline gap="4">
									<Button type="submit" disabled={submitting} size="lg">
										{submitting ? "Saving..." : "Save Service"}
									</Button>
									{onCancel ? (
										<Button
											onClick={onCancel}
											level="tertiary"
											size="lg"
											disabled={submitting}
										>
											Cancel
										</Button>
									) : null}
								</Inline>
							</Stack>
						</Col>
					</Grid>
				</form>
			)}
		</Form>
	)
}

export function LocationFields({ name }: { name: string }) {
	return (
		<FieldArray<TTransportServiceLocationPoint | string | undefined>
			name={name}
		>
			{({ fields }) => (
				<Stack gap="2">
					{fields.map((name, index) => (
						<Inline alignItems={"center"} gap="2">
							<Box style={{ width: "16rem" }}>
								<SelectField
									name={name}
									select={SelectTransportServiceLocationPoints}
									fetchOnMount
									multiple={false}
									creatable
								/>
							</Box>
							<Icons.Cancel
								color="danger"
								cursor="pointer"
								onClick={() => fields.remove(index)}
							></Icons.Cancel>
						</Inline>
					))}
					<Button
						style={{ width: "fit-content" }}
						size="sm"
						level="tertiary"
						onClick={() => fields.push(undefined)}
					>
						<Icons.Plus /> Add More
					</Button>
				</Stack>
			)}
		</FieldArray>
	)
}

export function AddTransportServiceDialog({
	open,
	onClose,
	onSuccess,
	...props
}: Optional<
	Omit<React.ComponentProps<typeof NewServicesForm>, "onCancel">,
	"onSuccess"
> & {
	open: boolean
	onClose: () => void
}) {
	return (
		<Dialog open={open} onClose={onClose} lg>
			<Dialog.Header closeButton>
				<Dialog.Title>Add new Transport Service</Dialog.Title>
			</Dialog.Header>
			<Dialog.Body>
				<NewServicesForm
					onSuccess={(...args) => {
						onClose()
						onSuccess?.(...args)
					}}
					onCancel={onClose}
					{...props}
				/>
			</Dialog.Body>
		</Dialog>
	)
}

function SelectClosedOnDaysOfWeekField({ name }: { name: string }) {
	return (
		<SelectField
			select={Select}
			name={name}
			label="Closed on Days of Week"
			secondaryLabel="optional"
			multiple
			options={[
				"Sunday",
				"Monday",
				"Tuesday",
				"Wednesday",
				"Thursday",
				"Friday",
				"Saturday",
			]}
			help="Please select the day(s) of week where this transport is closed or non-operational"
			placeholder="e.g. Monday, Tuesday"
		/>
	)
}

function SelectClosedOnDateIntervalsField({ name }: { name: string }) {
	return (
		<Stack gap="1">
			<Text fontWeight="semibold">Closed on Dates / Intervals</Text>
			<FieldArray name={name}>
				{({ fields }) => (
					<Stack flexWrap="wrap" gap="2">
						{fields.map((name, index) => (
							<Inline key={index} gap="4">
								<DateRangePickerField
									fromName={`${name}.start_date`}
									toName={`${name}.end_date`}
									rangePresets={null}
								/>
								<Box>
									<Button
										level="tertiary"
										onClick={() => {
											fields.remove(index)
										}}
									>
										<Icons.Cancel />
									</Button>
								</Box>
							</Inline>
						))}
						<Box>
							<Button
								size="sm"
								onClick={() => {
									fields.push({ start_date: undefined, end_date: undefined })
								}}
							>
								<Icons.Plus />{" "}
								{!fields.length ? "Add Date Intervals" : "Add More"}
							</Button>
						</Box>
					</Stack>
				)}
			</FieldArray>
			<Text color="muted" fontSize="sm">
				Select single date or intervals during which the transport is not
				operational.
			</Text>
		</Stack>
	)
}

export function HotelAsPickupDropToggleField({
	name,
	for: forLocation,
	clearFieldName,
	clearValue = null,
}: {
	name: string
	for?: "pickup" | "drop"
	clearFieldName?: string
	clearValue?: string | null
}) {
	const form = useForm()
	const { value } = useFieldValue<string | null>(name)

	const [selectedOption, setSelectedOption] = useState<string>("check_in")

	useEffect(() => {
		if (typeof value === "string") {
			switch (value) {
				case "check_in":
					setSelectedOption("check_in")
					break
				case "check_out":
					setSelectedOption("check_out")
					break
				default:
					setSelectedOption("check_in")
					break
			}
		} else {
			setSelectedOption("check_in")
		}
	}, [value])

	const checked = value === "check_in" || value === "check_out"

	const options = [
		{
			value: "check_in",
			label: `Use Check-in Hotel as ${
				forLocation === "pickup" ? "Pickup" : "Drop"
			}`,
		},
		{
			value: "check_out",
			label: `Use Check-out Hotel as ${
				forLocation === "pickup" ? "Pickup" : "Drop"
			}`,
		},
	]

	return (
		<Inline gap="1" alignItems="baseline">
			<Box as="label">
				<CheckboxInput
					checked={checked}
					name={name}
					onChange={(e) => {
						const isChecked = e.currentTarget.checked
						form.change(
							name as never,
							(isChecked ? selectedOption : null) as never
						)
						if (isChecked && clearFieldName) {
							form.change(clearFieldName as never, clearValue as never)
						}
					}}
				/>
			</Box>

			<Box position={"relative"}>
				<Box
					as="label"
					htmlFor={`${name}_options`}
					display="flex"
					alignItems="center"
					style={{ cursor: "pointer" }}
				>
					{`Use ${
						selectedOption === "check_in" ? "Check-in" : "Check-out"
					} Hotel as ${forLocation === "pickup" ? "Pickup" : "Drop"}`}{" "}
					<Icons.ChevronDown />
				</Box>

				<Box position="absolute" overflow="hidden" inset="0">
					<select
						id={`${name}_options`}
						value={selectedOption}
						style={{
							background: "transparent",
							opacity: 0,
							width: "100%",
							cursor: "pointer",
						}}
						onChange={(e) => {
							const newValue = e.currentTarget.value
							setSelectedOption(newValue)
							if (checked) {
								form.change(name as never, newValue as never)
							}
						}}
					>
						{options.map((option) => (
							<option key={option.value} value={option.value}>
								{option.label}
							</option>
						))}
					</select>
				</Box>
			</Box>
		</Inline>
	)
}
